/**
 * FinancialsPage.jsx
 * ==================
 *
 * Consultation fee + pharmacy sales breakdown for the clinic. Reads the
 * new /api/management/financials endpoint. Available to management, admin
 * and — auto-scoped to the caller's own visits — doctors as well.
 *
 * Layout mirrors the ManagementDashboard cockpit so the two feel cohesive:
 *   ▸ Range picker (Today / 7 / 30 / 90 days ...)
 *   ▸ Four KPI tiles (Consult total / Pharmacy total / Grand / Txns)
 *   ▸ Two side-by-side sections (Consultation Fees / Pharmacy Sales) each
 *     with by-doctor table, by-mode chips and a daily bar chart
 *   ▸ Download PDF button (respects the current period + scope)
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import {
  IndianRupee, Pill, Stethoscope, TrendingUp, FileDown, ArrowLeft, RefreshCw,
  Wallet, Users, LogOut, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDoctorName } from '../../utils/doctor';

const API = process.env.REACT_APP_BACKEND_URL;

const istNow = () => new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
const istDateStr = (d) => {
  const dt = new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const RANGE_PRESETS = [
  { v: 'today', l: 'Today', days: 0 },
  { v: '7', l: 'Last 7 days', days: 7 },
  { v: '30', l: 'Last 30 days', days: 30 },
  { v: '90', l: 'Last 90 days', days: 90 },
  { v: '180', l: 'Last 6 months', days: 180 },
  { v: '365', l: 'Last year', days: 365 },
];

function computeRange(preset) {
  const to = istNow();
  const from = istNow();
  const meta = RANGE_PRESETS.find(p => p.v === preset);
  from.setDate(from.getDate() - (meta?.days ?? 30));
  return { from: istDateStr(from), to: istDateStr(to), label: meta?.l ?? preset };
}

const inr = (n) => `₹${(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const KpiTile = ({ label, value, icon: Icon, hint, tone }) => {
  const tones = {
    blue: 'from-blue-600 to-indigo-700',
    teal: 'from-teal-600 to-emerald-700',
    amber: 'from-amber-500 to-orange-600',
    violet: 'from-violet-600 to-fuchsia-700',
  };
  return (
    <div
      className="rounded-2xl border border-zinc-200 bg-white p-5 hover:shadow-md transition-all"
      data-testid={`fin-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`h-9 w-9 rounded-xl bg-gradient-to-br ${tones[tone] || tones.blue} text-white grid place-items-center shadow-sm`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      <div className="text-2xl font-extrabold text-zinc-900" style={{ fontFamily: 'Manrope' }}>{value}</div>
      {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
};

const ByDoctorTable = ({ rows, valueLabel = 'Total' }) => {
  if (!rows || rows.length === 0) {
    return <div className="text-xs text-zinc-400 italic py-4">No entries in this period.</div>;
  }
  const max = Math.max(...rows.map(r => r.total)) || 1;
  return (
    <div className="space-y-2" data-testid="fin-by-doctor">
      {rows.map((r) => (
        <div key={r.doctor_id || r.doctor_name} className="flex items-center gap-3 text-sm">
          <span className="w-52 truncate text-zinc-800 font-medium">{formatDoctorName(r.doctor_name)}</span>
          <span className="flex-1 bg-zinc-100 rounded-full h-2 overflow-hidden">
            <span className="block h-full bg-gradient-to-r from-blue-600 to-teal-500 rounded-full"
              style={{ width: `${(r.total / max) * 100}%` }} />
          </span>
          <span className="text-xs text-zinc-500 w-14 text-right font-mono">{r.count}×</span>
          <span className="font-mono font-bold text-zinc-900 w-24 text-right">{inr(r.total)}</span>
        </div>
      ))}
      <div className="text-[10px] text-zinc-400 mt-1">Bar width = share of total {valueLabel.toLowerCase()}.</div>
    </div>
  );
};

const ByModeChips = ({ rows }) => {
  if (!rows || rows.length === 0) return null;
  const total = rows.reduce((a, r) => a + r.total, 0) || 1;
  return (
    <div className="flex flex-wrap gap-2" data-testid="fin-by-mode">
      {rows.map((r) => {
        const pct = Math.round((r.total / total) * 100);
        const tones = {
          cash: 'bg-emerald-100 text-emerald-800 border-emerald-200',
          upi: 'bg-violet-100 text-violet-800 border-violet-200',
          card: 'bg-blue-100 text-blue-800 border-blue-200',
        };
        const tone = tones[r.mode?.toLowerCase()] || 'bg-zinc-100 text-zinc-700 border-zinc-200';
        return (
          <span key={r.mode} className={`text-[11px] font-semibold rounded-full border px-2.5 py-1 ${tone}`}>
            <b className="capitalize">{r.mode}</b> · {inr(r.total)} · {pct}%
          </span>
        );
      })}
    </div>
  );
};

export default function FinancialsPage() {
  const { user, logout } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();

  const [preset, setPreset] = useState('30');
  const range = useMemo(() => computeRange(preset), [preset]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/management/financials?from=${range.from}&to=${range.to}`,
        { withCredentials: true },
      );
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load financial data');
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [preset]);

  const downloadPdf = () => {
    const url = `${API}/api/management/financials.pdf?from=${range.from}&to=${range.to}`;
    window.open(url, '_blank', 'noopener');
  };

  const consultation = data?.consultation || {};
  const pharmacy = data?.pharmacy || {};
  const isDoctorScope = data?.scope === 'mine';

  // Back-navigation target depends on role — doctor lands on /dashboard,
  // everyone else on /management. Keeps the report reachable from either.
  const backTo = (user?.role === 'doctor') ? '/dashboard' : '/management';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/40">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-700 via-teal-700 to-cyan-600" />
        <div className="absolute inset-0 opacity-[0.12]" style={{
          backgroundImage: 'linear-gradient(45deg, white 1px, transparent 1px), linear-gradient(-45deg, white 1px, transparent 1px)',
          backgroundSize: '22px 22px',
        }} />
        <div className="relative px-8 py-6 flex items-start justify-between text-white">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold opacity-90 mb-1">
              <Sparkles className="h-3 w-3" /> Financial Reports
            </div>
            <h1 className="text-2xl font-extrabold leading-tight" style={{ fontFamily: 'Manrope' }} data-testid="fin-title">
              {tenant?.name || 'Clinic'} — Fees & Sales
            </h1>
            <p className="text-[12px] opacity-85 mt-1">
              {isDoctorScope
                ? `Personal view — your consultation fees and pharmacy sales only.`
                : `Clinic-wide — all doctors, all payments. IST (Asia/Kolkata).`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate(backTo)}
              className="bg-white/15 border-white/30 text-white hover:bg-white/25"
              data-testid="fin-back">
              <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back
            </Button>
            <Select value={preset} onValueChange={setPreset}>
              <SelectTrigger className="w-40 bg-white/15 border-white/30 text-white hover:bg-white/25" data-testid="fin-range">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RANGE_PRESETS.map(r => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="sm" onClick={load}
              className="bg-white/15 border-white/30 text-white hover:bg-white/25" data-testid="fin-refresh">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
            </Button>
            <Button size="sm" onClick={downloadPdf}
              className="bg-white text-emerald-700 hover:bg-emerald-50 font-bold" data-testid="fin-download-pdf">
              <FileDown className="h-3.5 w-3.5 mr-1.5" /> Download PDF
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }}
              className="bg-white/0 border-white/0 text-white/85 hover:bg-white/10" data-testid="fin-logout">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="p-8 max-w-[1500px] mx-auto space-y-6">
        {/* KPI ROW */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiTile label="Consultation Fees" value={loading ? '…' : inr(consultation.total)}
            icon={Stethoscope} hint={`${consultation.count || 0} visits · avg ${inr(consultation.avg)}`} tone="blue" />
          <KpiTile label="Pharmacy Sales" value={loading ? '…' : inr(pharmacy.total)}
            icon={Pill} hint={`${pharmacy.count || 0} sales · avg ${inr(pharmacy.avg)}`} tone="teal" />
          <KpiTile label="Grand Total" value={loading ? '…' : inr(data?.grand_total)}
            icon={IndianRupee} hint={`${range.label}`} tone="amber" />
          <KpiTile label="Transactions" value={loading ? '…' : ((consultation.count || 0) + (pharmacy.count || 0))}
            icon={Wallet} hint="Paid txns in period" tone="violet" />
        </div>

        {/* Two-column: Consultation & Pharmacy */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Consultation */}
          <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid="fin-consultation-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Consultation Fees</div>
                <h3 className="text-lg font-bold text-zinc-900" style={{ fontFamily: 'Manrope' }}>
                  {inr(consultation.total)} <span className="text-sm font-normal text-zinc-500">· {consultation.count || 0} visits</span>
                </h3>
              </div>
              <TrendingUp className="h-5 w-5 text-emerald-600" />
            </div>
            {consultation.daily && consultation.daily.length > 0 && (
              <div style={{ height: 180 }} className="mb-4">
                <ResponsiveContainer>
                  <BarChart data={consultation.daily} margin={{ top: 4, right: 8, bottom: 12, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#475569' }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10, fill: '#475569' }} />
                    <Tooltip cursor={{ fill: '#eef2ff' }} contentStyle={{ borderRadius: 8, fontSize: 12 }}
                      formatter={(v) => inr(v)} />
                    <Bar dataKey="total" fill="#1d4ed8" radius={[4, 4, 0, 0]} maxBarSize={22} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mt-2 mb-2">By Doctor</div>
            <ByDoctorTable rows={consultation.by_doctor || []} />
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mt-4 mb-2">By Payment Mode</div>
            <ByModeChips rows={consultation.by_mode || []} />
          </div>

          {/* Pharmacy */}
          <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid="fin-pharmacy-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Pharmacy Sales</div>
                <h3 className="text-lg font-bold text-zinc-900" style={{ fontFamily: 'Manrope' }}>
                  {inr(pharmacy.total)} <span className="text-sm font-normal text-zinc-500">· {pharmacy.count || 0} sales</span>
                </h3>
              </div>
              <Pill className="h-5 w-5 text-teal-600" />
            </div>
            {pharmacy.daily && pharmacy.daily.length > 0 && (
              <div style={{ height: 180 }} className="mb-4">
                <ResponsiveContainer>
                  <BarChart data={pharmacy.daily} margin={{ top: 4, right: 8, bottom: 12, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#475569' }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10, fill: '#475569' }} />
                    <Tooltip cursor={{ fill: '#f0fdfa' }} contentStyle={{ borderRadius: 8, fontSize: 12 }}
                      formatter={(v) => inr(v)} />
                    <Bar dataKey="total" fill="#0d9488" radius={[4, 4, 0, 0]} maxBarSize={22} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mt-2 mb-2">By Prescribing Doctor</div>
            <ByDoctorTable rows={pharmacy.by_doctor || []} valueLabel="sales" />
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mt-4 mb-2">By Payment Mode</div>
            <ByModeChips rows={pharmacy.by_mode || []} />
            {pharmacy.top_medicines && pharmacy.top_medicines.length > 0 && (
              <>
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mt-4 mb-2">Top Medicines (by prescription count)</div>
                <ul className="text-sm space-y-1" data-testid="fin-top-medicines">
                  {pharmacy.top_medicines.slice(0, 8).map((m, i) => (
                    <li key={m.medicine} className="flex items-center gap-2">
                      <span className="text-[10px] font-mono text-zinc-400 w-6">#{i + 1}</span>
                      <span className="text-zinc-800 flex-1 truncate">{m.medicine}</span>
                      <span className="text-xs font-mono text-zinc-500">{m.prescriptions}×</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>

        {loading && (
          <div className="text-center text-zinc-400 text-sm animate-pulse py-4" data-testid="fin-loading">
            Crunching numbers…
          </div>
        )}
        {!loading && data && consultation.count === 0 && pharmacy.count === 0 && (
          <div className="text-center py-16 rounded-xl border border-dashed border-zinc-300 bg-white">
            <Users className="h-10 w-10 mx-auto text-zinc-300 mb-2" />
            <p className="text-sm text-zinc-500">No transactions recorded in this period.</p>
          </div>
        )}
      </div>
    </div>
  );
}
